<template>
  <div>
    <ModalBoleto
      v-model="abreModal"
      :titulo="'Boleto Projeto - ' + sqProjetoVar"
      :src="urlPagamento"
    />
    <ModalQrCode
      :valor="vlAstec"
      v-model="mostraQRCode"
      :qrCodeImage="qrCodeImage"
      :textAreaData="textAreaData"
      :dadosPixDoProjeto="dadosPixDoProjeto"
      :titulo="'QR Code Pix Projeto - ' + sqProjetoVar"
    />

    <PageHeader :title="titleHeader" />
    <div class="text-center" v-if="loading">
      <b-spinner class="ms-2" variant="primary"></b-spinner>
    </div>
    <div class="card" v-if="!loading">
      <div class="card-body">
        <div class="row d-flex justify-content-between">
          <div class="col-md-8">
            <b-card-title>
              <h5 class="card-title mb-3">
                Informações do Projeto -
                <span
                  v-if="!importacao"
                  class="badge badge-pill font-size-15"
                  :class="{
                    'badge-soft-info': statusProjeto === 'ELABORADO',
                    'badge-soft-success': statusProjeto === 'LIBERADO',
                    'badge-soft-dark': statusProjeto === 'LIQUIDADO',
                    'badge-soft-danger': statusProjeto === 'EXCLUIDO',
                    'badge-soft-warning': statusProjeto === 'BAIXA PARCIAL'
                  }"
                >
                  {{ statusProjeto }}
                </span>

                <span
                  v-else
                  class="badge badge-pill font-size-12"
                  :class="{
                    'badge-soft-info': statusProjeto === 'COMPLETO',
                    'badge-soft-success': statusProjeto === 'EXPORTADO',
                    'badge-soft-danger': statusProjeto === 'INCOMPLETO'
                  }"
                >
                  {{ statusProjeto }}
                </span>
                <span v-if="sqProjetoVar"> - {{ sqProjetoVar }}</span>
              </h5>
            </b-card-title>
          </div>
          <div
            class="col-md-4 d-flex justify-content-end mb-3"
            v-if="podeDuplicar && !importacao"
          >
            <b-button variant="primary" @click="duplicarProjeto()">
              <i class="bx bx-copy"></i>
              Duplicar Informações
            </b-button>
          </div>
        </div>

        <div class="row d-flex mb-3">
          <div class="col">
            <b-form-group label="Cooperado" :state="state('selecaoCooperado')">
              <multiselect
                v-model="selecaoCooperado"
                :options="cooperadoLkp"
                label="name"
                track-by="value"
                placeholder="Selecione um cooperado"
                selected-label="Selecionado"
                deselect-label="Pressione Enter para remover"
                select-label="Pressione Enter para selecionar"
                :allowEmpty="false"
                :taggable="false"
                @input="carregarClienteRural"
                invalid-feedback="Campo obrigatório"
                :disabled="disabledCampos || importacao || emEdicao"
              >
                <span slot="noResult">Nenhum cooperado encontrado</span>
                <span slot="noOptions">Nenhum cooperado disponível</span>
              </multiselect>
            </b-form-group>
          </div>
          <div class="col">
            <b-form-group
              label="Cliente Rural"
              :state="state('selecaoClienteRural')"
            >
              <multiselect
                v-model="selecaoClienteRural"
                :options="clienteRuralLkp"
                label="name"
                track-by="value"
                placeholder="Selecione um cliente rural"
                selected-label="Selecionado"
                deselect-label="Pressione Enter para remover"
                select-label="Pressione Enter para selecionar"
                invalid-feedback="Campo obrigatório"
                :disabled="disabledCampos || importacao"
                :loading="loadingClienteRural"
              >
                <span slot="noResult">Nenhum cliente rural encontrado</span>
                <span slot="noOptions">Nenhum cliente rural disponível</span>
              </multiselect>
            </b-form-group>
          </div>
          <div class="col">
            <b-form-group
              label="CPF/CNPJ"
              :state="state('selecaoClienteRural')"
            >
              <multiselect
                label="cpfCnpj"
                invalid-feedback="Campo obrigatório"
                :disabled="disabledCampos || importacao"
                v-model="selecaoClienteRural"
                :options="filteredOptions"
                :custom-label="formatCpfCnpj"
                track-by="value"
                placeholder="Selecione um CPF/CNPJ"
                selected-label="Selecionado"
                deselect-label="Pressione Enter para remover"
                select-label="Pressione Enter para selecionar"
                :loading="loadingClienteRural"
                :max-height="200"
                :internal-search="false"
                @search-change="updateSearch"
              >
                <span slot="noResult">Nenhum CPF/CNPJ encontrado</span>
                <span slot="noOptions">Nenhum CPF/CNPJ disponível</span>
              </multiselect>
            </b-form-group>
          </div>
          <div class="col">
            <b-form-group
              label="N° Projeto"
              invalid-feedback="Campo obrigatório"
            >
              <b-form-input
                v-model="numeroProjeto"
                v-mask="'########'"
                type="text"
                :maxlength="8"
                :state="state('numeroProjeto')"
                :disabled="disabledCampos || importacao"
                placeholder="Digite o número do projeto"
              />
            </b-form-group>
          </div>
          <div class="col">
            <b-form-group
              label="Data do Projeto"
              :state="state('dataProjeto')"
              invalid-feedback="Campo obrigatório"
            >
              <date-picker
                append-to-body
                format="DD/MM/YYYY"
                v-model="dataProjeto"
                placeholder="DD/MM/YYYY"
                value-type="YYYY-MM-DD HH:mm:ss"
                @input="disableDatasPosteriores()"
                :disabled-date="disableDatasFuturas"
                :disabled="disabledCampos || importacao"
                :class="{ 'opacity-date': disabledCampos || importacao }"
              />
            </b-form-group>
          </div>
        </div>
        <div class="row d-flex mb-3">
          <div class="col-md-3">
            <b-form-group
              label="Instituição Financeira"
              :state="state('selecaoInstituicaoFinanceira')"
              invalid-feedback="Campo obrigatório"
            >
              <multiselect
                label="name"
                track-by="name"
                @input="carregarAgencia"
                selected-label="Selecionado"
                :loading="loadingInstFinanceira"
                :options="instituicaoFinanceiraLkp"
                v-model="selecaoInstituicaoFinanceira"
                :disabled="disabledCampos || importacao"
                deselect-label="Pressione Enter para remover"
                select-label="Pressione Enter para selecionar"
                placeholder="Selecione uma instituição financeira"
              >
                <span slot="noResult"
                  >Nenhuma instituição financeira encontrada</span
                >
                <span slot="noOptions"
                  >Nenhuma instituição financeira disponível</span
                >
              </multiselect>
            </b-form-group>
          </div>
          <div class="col-md-3">
            <b-form-group
              label="Agência"
              :state="state('selecaoAgencia')"
              invalid-feedback="Campo obrigatório"
            >
              <multiselect
                v-model="selecaoAgencia"
                :loading="loadingAgencia"
                :disabled="disabledCampos"
                :options="instituicaoAgenciaLkp"
                label="name"
                track-by="name"
                placeholder="Selecione uma agência"
                selected-label="Selecionado"
                deselect-label="Pressione Enter para remover"
                select-label="Pressione Enter para selecionar"
              >
                <span slot="noResult">Nenhuma agência encontrada</span>
                <span slot="noOptions">Nenhuma agência disponível</span>
              </multiselect>
            </b-form-group>
          </div>
          <div class="col-md-3">
            <b-form-group
              label="Município - UF"
              :state="state('selecaoMunicipio')"
              invalid-feedback="Campo obrigatório"
            >
              <multiselect
                v-model="selecaoMunicipio"
                :disabled="disabledCampos"
                :options="selecaoMunicipioLkp"
                label="name"
                track-by="name"
                placeholder="Selecione um município"
                selected-label="Selecionado"
                deselect-label="Pressione Enter para remover"
                select-label="Pressione Enter para selecionar"
              >
                <span slot="noResult">Nenhum município encontrado</span>
                <span slot="noOptions">Nenhum município disponível</span>
              </multiselect>
            </b-form-group>
          </div>

          <div class="col-md-3">
            <b-form-group
              label="Linha de Crédito"
              :state="state('selecaoLinhaCredito')"
              invalid-feedback="Campo obrigatório"
            >
              <multiselect
                v-model="selecaoLinhaCredito"
                :options="linhaCreditoLkp"
                label="name"
                track-by="name"
                placeholder="Selecione uma linha de crédito"
                selected-label="Selecionado"
                deselect-label="Pressione Enter para remover"
                select-label="Pressione Enter para selecionar"
                :disabled="disabledCampos || importacao"
              >
                <span slot="noOptions">
                  Nenhuma linha de crédito disponível
                </span>
                <span slot="noResult">
                  Nenhuma linha de crédito encontrada
                </span>
              </multiselect>
            </b-form-group>
          </div>
        </div>
        <div class="row d-flex mb-3">
          <div class="col-md-3">
            <b-form-group
              label="Safra"
              :state="state('selecaoSafra')"
              invalid-feedback="Campo obrigatório"
            >
              <multiselect
                v-model="selecaoSafra"
                :disabled="disabledCampos || importacao"
                :options="selecaoSafraLkp"
                label="name"
                track-by="name"
                placeholder="Selecione uma safra"
                selected-label="Selecionado"
                deselect-label="Pressione Enter para remover"
                select-label="Pressione Enter para selecionar"
                @input="carregarItemAgropecuario"
              >
                <span slot="noResult">Nenhuma safra encontrada</span>
                <span slot="noOptions">Nenhuma safra disponível</span>
              </multiselect>
            </b-form-group>
          </div>

          <div class="col-md-3">
            <b-form-group
              label="Tipo de Projeto"
              :state="state('selecaoTipoProjeto')"
              invalid-feedback="Campo obrigatório"
            >
              <multiselect
                v-model="selecaoTipoProjeto"
                :options="tipoProjetoLkp"
                label="name"
                track-by="name"
                placeholder="Selecione um tipo de projeto"
                selected-label="Selecionado"
                deselect-label="Pressione Enter para remover"
                select-label="Pressione Enter para selecionar"
                @input="carregarItemAgropecuario"
                :disabled="disabledCampos"
              >
                <span slot="noResult">Nenhum tipo de projeto encontrado</span>
                <span slot="noOptions">Nenhum tipo de projeto disponível</span>
              </multiselect>
            </b-form-group>
          </div>

          <div class="col-md-3">
            <b-form-group
              label="Item Agropecuário"
              :state="state('selecaoItemAgropecuario')"
              invalid-feedback="Campo obrigatório"
            >
              <multiselect
                v-model="selecaoItemAgropecuario"
                :disabled="disabledCampos"
                :options="itemAgropecuarioLkp"
                :loading="loadingItemAgropecuario"
                label="name"
                track-by="name"
                placeholder="Selecione um item agropecuário"
                selected-label="Selecionado"
                deselect-label="Pressione Enter para remover"
                select-label="Pressione Enter para selecionar"
              >
                <span slot="noOptions">
                  Lista vazia, selecione a safra e tipo de projeto.
                </span>
                <span slot="noResult">
                  Nenhum item agropecuário encontrado para a safra e tipo de
                  projeto selecionados.
                </span>
              </multiselect>
            </b-form-group>
          </div>

          <div class="col-md-3">
            <b-form-group
              label="Área (ha)"
              invalid-feedback="Campo obrigatório"
              :state="state('areaHa')"
            >
              <money
                v-model="areaHa"
                v-bind="area"
                class="form-control"
                :state="state('areaHa')"
                :disabled="disabledCampos || importacao"
                placeholder="Digite a área em hectares"
              >
              </money>
            </b-form-group>
          </div>
        </div>
        <div class="row d-flex mb-3">
          <div class="col-md-3">
            <b-form-group
              label="Modalidade do Seguro"
              :state="state('selecaoModalidadeSeguro')"
              invalid-feedback="Campo obrigatório"
            >
              <multiselect
                label="name"
                track-by="name"
                selected-label="Selecionado"
                v-model="selecaoModalidadeSeguro"
                :options="selecaoModalidadeSeguroLkp"
                placeholder="Selecione uma modalidade"
                :disabled="disabledCampos || importacao"
                deselect-label="Pressione Enter para remover"
                select-label="Pressione Enter para selecionar"
              >
                <span slot="noResult"
                  >Nenhuma modalidade de seguro encontrada</span
                >
                <span slot="noOptions"
                  >Nenhuma modalidade de seguro disponível</span
                >
              </multiselect>
            </b-form-group>
          </div>

          <div class="col-md-3">
            <b-form-group
              label="Valor Orçado"
              :state="state('vlOrcado')"
              invalid-feedback="Campo obrigatório"
            >
              <money
                v-bind="money"
                v-model="vlOrcado"
                class="form-control"
                :state="state('vlOrcado')"
                placeholder="Digite o valor orçado"
                :disabled="disabledCampos || importacao"
                @input="calculoAstec.onValorOrcadoChange"
              />
            </b-form-group>
          </div>

          <div class="col-md-3">
            <b-form-group
              label="% Astec"
              :state="state('perAstec')"
              invalid-feedback="Campo obrigatório"
            >
              <b-form-input
                v-model="percentualDisplay"
                v-money="porcentagem"
                :maxlength="8"
                :state="state('perAstec')"
                placeholder="Digite o percentual"
                :disabled="disabledCampos || importacao"
                @input="calculoAstec.onPercentualChange"
              />
            </b-form-group>
          </div>

          <div class="col-md-3">
            <b-form-group
              label="Valor Astec"
              :state="state('vlAstec')"
              invalid-feedback="Campo obrigatório"
            >
              <money
                v-bind="money"
                v-model="vlAstec"
                :state="state('vlAstec')"
                class="form-control money-mask"
                placeholder="Digite o valor Astec"
                :disabled="disabledCampos || importacao"
                @input="calculoAstec.onValorAstecChange"
              />
            </b-form-group>
          </div>
        </div>

        <div v-if="!importacao" class="row d-flex mb-3">
          <div class="col">
            <b-form-group label="Projeto Unicampo">
              <multiselect
                v-model="selecaoProjeto"
                :options="selecaoProjetoLkp"
                label="name"
                track-by="name"
                placeholder="Selecione um projeto"
                selected-label="Selecionado"
                deselect-label="Pressione Enter para remover"
                select-label="Pressione Enter para selecionar"
                :disabled="true"
                :max-height="150"
              />
            </b-form-group>
          </div>

          <div class="col">
            <b-form-group
              label="N° Operação"
              invalid-feedback="Campo obrigatório"
            >
              <b-form-input
                v-model="nOperacao"
                v-mask="'##################'"
                :maxlength="30"
                type="text"
                :disabled="disabledCampos"
                placeholder="Digite o número da operação"
              />
            </b-form-group>
          </div>

          <div class="col">
            <b-form-group
              label="Data Vencimento"
              invalid-feedback="Campo obrigatório"
            >
              <date-picker
                append-to-body
                format="DD/MM/YYYY"
                v-model="dataVencBoleto"
                placeholder="DD/MM/YYYY"
                :disabled="disabledCampos"
                value-type="YYYY-MM-DD HH:mm:ss"
                :disabled-date="disableDatasAnteriores"
              />
            </b-form-group>
          </div>

          <div class="col">
            <b-form-group
              label="Valor Liberado"
              invalid-feedback="Campo obrigatório"
            >
              <money
                v-model="vlLiberado"
                v-bind="money"
                class="form-control"
                :disabled="true"
                placeholder="Valor liberado"
              >
              </money>
            </b-form-group>
          </div>

          <div class="col">
            <b-form-group
              label="Data Crédito"
              invalid-feedback="Campo obrigatório"
            >
              <date-picker
                append-to-body
                :disabled="true"
                class="opacity-75"
                format="DD/MM/YYYY"
                v-model="dataCredito"
                placeholder="DD/MM/YYYY"
                value-type="YYYY-MM-DD HH:mm:ss"
              />
            </b-form-group>
          </div>
        </div>

        <div class="row d-flex justify-content-between mb-1 align-items-center">
          <div class="col-md-6">
            <div v-if="!importacao" class="d-flex gap-2">
              <b-button
                v-if="sqProjetoVar"
                :disabled="
                  loadingBoleto ||
                    loadingGeral ||
                    !podeDuplicar ||
                    disabledCampos
                "
                size="md"
                variant="light"
                @click="gerarBoleto"
                class="mb-2 mb-md-0"
                data-tooltip="Gerar Boleto"
                data-tooltip-location="bottom"
                style="width: 45px; height: 37px"
              >
                <i v-if="!loadingBoleto" class="mdi mdi-barcode"></i>
                <b-spinner
                  class="m-0"
                  small
                  variant="success"
                  v-if="loadingBoleto"
                ></b-spinner>
              </b-button>

              <b-button
                v-if="sqProjetoVar"
                :disabled="
                  loadingPix || loadingGeral || !podeDuplicar || disabledCampos
                "
                size="md"
                variant="light"
                @click="gerarPix"
                class="mb-2 mb-md-0"
                data-tooltip="Gerar Pix"
                data-tooltip-location="bottom"
                style="width: 45px; height: 37px"
              >
                <img
                  height="18"
                  alt="Pix Brands"
                  v-if="!loadingPix"
                  :src="require('@/assets/images/pix-brands-solid.svg')"
                />
                <b-spinner
                  class="m-0"
                  small
                  variant="success"
                  v-if="loadingPix"
                ></b-spinner>
              </b-button>

              <b-button
                v-show="statusTipoProjeto(statusProjeto)"
                :disabled="!sqProjetoVar > 0 || loadingSalvar"
                size="md"
                variant="light"
                data-tooltip="Movimentações"
                data-tooltip-location="bottom"
                @click="openModalMovimentacao"
              >
                <div v-if="!loadingMovimentacao">
                  <i class="fa fa-solid fa-wallet"></i>
                </div>
                <b-spinner
                  small
                  class="mt-0"
                  variant="success"
                  v-if="loadingMovimentacao"
                ></b-spinner>
              </b-button>
            </div>
          </div>

          <div
            class="col-md-6 d-flex justify-content-end align-items-center gap-2"
          >
            <b-button
              size="md"
              @click="voltar"
              variant="secondary"
              class="mb-2 mb-md-0"
              data-tooltip="Voltar"
              :disabled="loadingGeral"
              data-tooltip-location="bottom"
            >
              <i class="mdi mdi-arrow-left-circle font-size-15"></i>
              <span class="ms-1">Voltar</span>
            </b-button>
            <b-button
              size="md"
              variant="success"
              data-tooltip="Salvar e Voltar"
              data-tooltip-location="bottom"
              @click="insertProjeto('voltar')"
              :disabled="loadingGeral || disabledCampos"
              class="mb-2 mb-md-0 d-flex align-items-center justify-content-center button-style"
            >
              <div v-if="!loadingSalvarVoltar">
                <i class="mdi mdi-arrow-left-circle font-size-15"></i>
                <span class="ms-1">Salvar e Voltar</span>
              </div>
              <b-spinner
                small
                variant="white"
                v-if="loadingSalvarVoltar"
              ></b-spinner>
            </b-button>

            <b-button
              v-if="!importacao && !editandoProjeto"
              size="md"
              variant="success"
              data-tooltip-location="bottom"
              data-tooltip="Salvar e Continuar"
              @click="insertProjeto('continuar')"
              style="width: 175px; height: 37px"
              class="mb-2 mb-md-0 d-flex align-items-center justify-content-center"
              :disabled="
                loadingSalvarContinuar || disabledCampos || editandoProjeto
              "
            >
              <span v-if="!loadingSalvarContinuar">
                <i class="mdi mdi-content-save font-size-15"></i>
                <span class="ms-1">Salvar e Continuar</span>
              </span>
              <b-spinner
                small
                variant="white"
                v-if="loadingSalvarContinuar"
              ></b-spinner>
            </b-button>

            <b-button
              size="md"
              variant="success"
              data-tooltip="Salvar"
              data-tooltip-location="bottom"
              @click="insertProjeto('salvar')"
              style="width: 85px; height: 37px"
              v-else-if="!importacao && editandoProjeto"
              :disabled="loadingGeral || disabledCampos"
              class="d-flex align-items-center justify-content-center"
            >
              <div v-if="!loadingSalvarNormal">
                <i class="me-1 mdi mdi-content-save"></i>
                <span>Salvar</span>
              </div>
              <b-spinner
                small
                variant="white"
                v-if="loadingSalvarNormal"
              ></b-spinner>
            </b-button>

            <b-button
              v-else
              size="md"
              variant="info"
              data-tooltip-location="bottom"
              data-tooltip="Salvar e Exportar"
              @click="insertProjeto('exportar')"
              :disabled="loadingSalvarExportar || disabledCampos"
              class="button-style d-flex align-items-center justify-content-center"
            >
              <span v-if="!loadingSalvarExportar">
                <i class="mdi mdi-content-save font-size-15"></i>
                <span class="ms-1">Salvar e Exportar</span>
              </span>

              <b-spinner
                small
                variant="white"
                v-if="loadingSalvarExportar"
              ></b-spinner>
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <formDadosImportados v-if="importacao && !loading" />
    <ModalMovimentacao
      :isOpenModal.sync="showModalMovimentacao"
      :dadosMovimentacao="retornoMovimentacao"
    />
  </div>
</template>

<script>
import Swal from "sweetalert2";
import { Money } from "v-money";
import Multiselect from "vue-multiselect";
import PageHeader from "@/components/page-header";
import ModalQrCode from "@/components/astec/modalQrCode";
import clienteRuralMixin from "@/tools/mixins/clienteRuralMixin.js";
import { MovimentacaoService } from "@/services/astec/movimentacao";
import { required, requiredIf, minValue } from "vuelidate/lib/validators";
import { CalculoAstecService } from "../../../../services/astec/projetos";
import ModalBoleto from "@/components/assinaturaEletronica/modalAssinatura";
import { mapearDadosFormularioProjeto } from "@/utils/astec/projetoPixMapearCampos";
import ModalMovimentacao from "@/components/astec/projetos/movimentacao/modalMovimentacao";
import formDadosImportados from "@/components/astec/projetos/lotesImportacao/formDadosImportados";

export default {
  mixins: [clienteRuralMixin],
  components: {
    Money,
    PageHeader,
    ModalBoleto,
    Multiselect,
    ModalQrCode,
    ModalMovimentacao,
    formDadosImportados
  },
  data() {
    return {
      statusProjeto: "",
      selecaoCooperado: null,
      cooperadoLkp: [],
      clienteRuralLkp: [],
      selecaoClienteRural: null,
      selecaocpfCnpj: "",
      cpfCnpjInvalido: false,
      numeroProjeto: "",
      dataProjeto: null,
      linhaCreditoLkp: [],
      selecaoLinhaCredito: null,
      selecaoTipoProjeto: null,
      tipoProjetoLkp: [],
      selecaoModalidadeSeguro: null,
      selecaoModalidadeSeguroLkp: [],
      StatusLkp: [],
      selecaoStatus: null,
      itemAgropecuarioLkp: [],
      selecaoItemAgropecuario: null,
      selecaoMunicipio: null,
      selecaoMunicipioLkp: [],
      selecaoSafra: null,
      selecaoSafraLkp: [],
      selecaoProjeto: null,
      selecaoProjetoLkp: [],
      areaHa: "",
      nOperacao: "",
      vlOrcado: "",
      perAstec: "",
      vlAstec: "",
      vlLiberado: "",
      dataCredito: null,
      instituicaoFinanceiraLkp: [],
      selecaoInstituicaoFinanceira: null,
      loadingInstFinanceira: false,
      loadingClienteRural: false,
      loadingAgencia: false,
      loadingItemAgropecuario: false,
      instituicaoAgenciaLkp: [],
      selecaoAgencia: null,
      dataVencBoleto: null,
      cdPessoa: "",
      sqUsuario: "",
      cdProjeto: 30,
      sqProjetoVar: null,
      loading: true,
      loadingSalvar: false,
      loadingBoleto: false,
      loadingPix: false,
      submit: false,
      disabledCampos: false,
      titleHeader: "",
      money: {
        decimal: ",",
        thousands: ".",
        precision: 2,
        prefix: "R$ ",
        masked: false,
        allowBlank: false,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      },
      area: {
        decimal: ",",
        thousands: ".",
        suffix: " (ha)",
        precision: 2,
        masked: false
      },
      porcentagem: {
        prefix: "",
        decimal: ",",
        suffix: " %",
        precision: 2,
        masked: false
      },
      searchQuery: "",
      dadosAoCarregar: null,
      editandoProjeto: false,
      importacao: false,
      abreModal: false,
      urlPagamento: "",
      mostraQRCode: false,
      qrCodeImage: "",
      textAreaData: "",

      loadingGeral: false,
      loadingSalvarNormal: false,
      loadingSalvarVoltar: false,
      loadingMovimentacao: false,
      loadingSalvarExportar: false,
      loadingSalvarContinuar: false,

      retornoMovimentacao: null,
      showModalMovimentacao: false,
      cdCooperadoSelecionado: null,
      dadosPixDoProjeto: null,
      retornoInsertProjeto: false,
      calculoAstec: null,
      percentualDisplay: "0,00 %"
    };
  },

  async created() {
    this.calculoAstec = CalculoAstecService.criarControlador(
      this,
      this.percentualDisplay
    );

    this.importacao = localStorage.getItem("exibirCamposImportacao") === "true";

    await this.buscarDados();

    if (this.$store.state.projetoAstec) {
      const projeto = this.$store.state.projetoAstec;
      this.titleHeader = "Editando Projeto Astec";
      this.cdProjeto = projeto.CD_PROJETO;
      this.sqProjetoVar = projeto.SQ_PROJETO_VAR;
      this.editandoProjeto = true;

      if (this.importacao) {
        this.statusProjeto = projeto.ST_IMPORTACAO;
      } else {
        this.statusProjeto = projeto.ST_PROJETO;
      }

      if (
        projeto.ST_PROJETO !== "ELABORADO" &&
        projeto.ST_IMPORTACAO !== "INCOMPLETO" &&
        projeto.ST_IMPORTACAO !== "COMPLETO"
      ) {
        this.titleHeader = "Consultando Projeto Astec";
        this.disabledCampos = true;
      }

      await this.preencherCampos(projeto);
    } else {
      this.titleHeader = "Criando Projeto Astec";
      this.statusProjeto = "ELABORADO";
    }

    this.selecaoProjeto = this.selecaoProjetoLkp.find(
      el => el.value == this.cdProjeto
    );

    this.$tools.fecharMenu();

    this.$nextTick(() => {
      setTimeout(() => {
        this.dadosAoCarregar = this.getSnapshotParaComparacao();
      }, 350);
    });

    if (this.importacao && this.statusProjeto === "INCOMPLETO") {
      this.submit = true;
      this.$nextTick(() => {
        if (this.$v && this.$v.$touch) this.$v.$touch();
      });
    }
  },

  computed: {
    filteredOptions() {
      if (!this.searchQuery) return this.clienteRuralLkp;
      const normalizedSearch = this.normalizeCpfCnpj(this.searchQuery);

      return this.clienteRuralLkp.filter(option => {
        const normalizedOption = this.normalizeCpfCnpj(option.cpfCnpj);
        return normalizedOption.includes(normalizedSearch);
      });
    },
    emEdicao() {
      return !!this.$store.state.projetoAstec;
    },
    podeDuplicar() {
      if (this.editandoProjeto || this.importacao) {
        return true;
      }

      if (this.$store.state.projetoAstec) {
        const projeto = this.$store.state.projetoAstec;
        return (
          (this.cdProjeto === 30 && projeto.CD_PESSOA_CORRETORA === 0) ||
          projeto.CD_PESSOA_CORRETORA == null
        );
      }

      return false;
    }
  },

  validations: {
    selecaoCooperado: { required },
    selecaoClienteRural: { required },
    numeroProjeto: { required },
    dataProjeto: { required },
    selecaoLinhaCredito: { required },
    selecaoModalidadeSeguro: { required },
    selecaoMunicipio: { required },
    selecaoSafra: { required },
    areaHa: { required, minValue: minValue(0.01) },
    vlOrcado: { required, minValue: minValue(1) },
    perAstec: { required },
    vlAstec: { required },
    selecaoInstituicaoFinanceira: { required },

    selecaoTipoProjeto: {
      required: requiredIf(function() {
        return (
          (this.importacao && this.statusProjeto === "INCOMPLETO") || required
        );
      })
    },

    selecaoItemAgropecuario: {
      required: requiredIf(function() {
        return (
          (this.importacao && this.statusProjeto === "INCOMPLETO") || required
        );
      })
    },

    selecaoAgencia: {
      required: requiredIf(function() {
        return (
          (this.importacao && this.statusProjeto === "INCOMPLETO") || required
        );
      })
    }
  },

  methods: {
    getSnapshotParaComparacao() {
      const raw = this.montarObjeto();
      const normalizado = {
        ...raw,
        qtArea:
          raw.qtArea !== null && raw.qtArea !== "" ? Number(raw.qtArea) : null,
        vlOrcado: this.vlOrcado ? Number(this.vlOrcado) : null,
        vlAstec: this.vlAstec ? Number(this.vlAstec) : null,
        perAstec: this.perAstec || null,
        dtProjeto: raw.dtProjeto || null,
        dtCredito: raw.dtCredito || null,
        dtVctoFinanc: raw.dtVctoFinanc || null
      };
      return JSON.stringify(normalizado);
    },

    async buscarDados() {
      await this.getNomeRazaoUsuario();
      await this.buscarLkpCooperado();
      await this.verificarCooperado();
      await this.buscarLkpLinhaCredito();
      await this.buscarLkpTipoProjeto();
      await this.buscarLkpModalidadeSeguro();
      await this.buscarMunicipioUf();
      await this.buscarLkpSafra();
      await this.buscarLkpProjeto();
      await this.carregarClienteRural();

      this.loading = false;
    },

    state(param) {
      if (this.submit) {
        return !this.$v[param].$invalid;
      } else {
        return null;
      }
    },

    disableDatasFuturas(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date > today;
    },

    disableDatasPosteriores(date) {
      if (this.dataProjeto) {
        const today = new Date(this.dataProjeto);
        today.setHours(0, 0, 0, 0);
        return date < today;
      }
      return date;
    },

    disableDatasAnteriores(date) {
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      return date < hoje;
    },

    formatarValorParaInput(valor) {
      if (valor === null || valor === undefined || valor === "") {
        return null;
      }

      if (typeof valor === "number") {
        return valor;
      }

      if (typeof valor === "string") {
        const limpo = valor.replace(/[^\d,.-]/g, "");
        if (limpo === "") {
          return null;
        }
        let numero;
        if (limpo.includes(",")) {
          const semMilhar = limpo.replace(/\./g, "");
          numero = parseFloat(semMilhar.replace(",", "."));
        } else {
          numero = parseFloat(limpo);
        }
        return isNaN(numero) ? null : numero;
      }
      return null;
    },

    async preencherCampos(projeto) {
      if (projeto) {
        try {
          this.calculoAstec.setValoresIniciais(
            projeto.VL_ORCADO,
            projeto.PER_ASTEC,
            projeto.VL_ASTEC
          );

          this.selecaoCooperado = this.cooperadoLkp.find(
            el => el.value == projeto.CD_COOPERADO
          );

          await this.carregarClienteRural();

          this.selecaoClienteRural = this.encontrarClienteRuralDoProjeto(
            projeto
          );
          this.numeroProjeto = projeto.NR_PROJETO;
          this.dataProjeto = projeto.DT_PROJETO;

          this.selecaoLinhaCredito = this.linhaCreditoLkp.find(
            el => el.value == projeto.CD_LINHA_CREDITO
          );

          this.selecaoTipoProjeto = this.tipoProjetoLkp.find(
            el => el.name.split(" [")[0] === projeto.DS_TP_PROJETO
          );

          this.selecaoSafra = this.selecaoSafraLkp.find(
            el => el.name == projeto.CD_SAFRA || el.name == projeto.DS_SAFRA
          );

          await this.carregarItemAgropecuario();

          this.selecaoItemAgropecuario = this.itemAgropecuarioLkp.find(
            el => el.name.split(" [")[0] == projeto.DS_ITEM_AGROPECUARIO
          );

          if (projeto.CD_MODALIDADE_SEGURO === null) {
            this.selecaoModalidadeSeguro = this.selecaoModalidadeSeguroLkp.find(
              el => el.value === "3"
            );
          } else {
            this.selecaoModalidadeSeguro = this.selecaoModalidadeSeguroLkp.find(
              el => el.value == projeto.CD_MODALIDADE_SEGURO
            );
          }

          this.selecaoMunicipio = this.selecaoMunicipioLkp.find(
            el => el.value == projeto.SQ_REGIAO_PROJETO
          );

          this.areaHa = this.formatarValorParaInput(projeto.QT_AREA);
          this.nOperacao = projeto.CD_OPERACAO_CRED;
          this.vlLiberado = this.formatarValorParaInput(projeto.VL_LIBERADO);
          this.dataCredito = projeto.DT_CREDITO_BCO;

          this.selecaoInstituicaoFinanceira = this.instituicaoFinanceiraLkp.find(
            el => el.value == projeto.CD_INSTITUICAO_FINANCEIRA
          );

          await this.carregarAgencia();

          this.selecaoAgencia = this.instituicaoAgenciaLkp.find(
            el => el.value == projeto.NR_AGENCIA
          );

          this.dataVencBoleto = projeto.DT_VENC_BOLETO;
        } catch (error) {
          console.error("Erro ao preencher campos:", error);
        }
      } else {
        this.$tools.alerta(
          "Ocorreu um erro ao preencher os campos, por favor tente novamente.",
          "error"
        );
      }
    },

    encontrarClienteRuralDoProjeto(projeto) {
      if (!projeto || !Array.isArray(this.clienteRuralLkp)) return null;

      const valoresProjeto = this.extrairValoresProjeto(projeto);
      const codigoProjeto = `${projeto.SQ_CLIENTE ||
        projeto.CD_CLIENTE ||
        projeto.ID_CLIENTE ||
        projeto.ID ||
        projeto.CODIGO_CLIENTE ||
        ""}`;
      const cpfCnpjProjeto = this.normalizeCpfCnpj(
        projeto.NRCPFCNPJ ||
          projeto.NR_CPF_CNPJ ||
          projeto.CPF_CNPJ ||
          projeto.CPFCNPJ
      );
      const nomeProjeto = this.normalizeTexto(
        projeto.NMRAZAO ||
          projeto.NM_RAZAO ||
          projeto.DS_CLIENTE_RURAL ||
          projeto.CLIENTE_RURAL ||
          ""
      );

      return (
        this.clienteRuralLkp.find(
          el => this.extrairCodigoClienteRural(el) === codigoProjeto
        ) ||
        this.clienteRuralLkp.find(
          el => `${el.value}` === `${projeto.SQ_CLIENTE}`
        ) ||
        this.clienteRuralLkp.find(el => {
          if (!el?.cpfCnpj || !cpfCnpjProjeto) {
            return false;
          }
          return this.normalizeCpfCnpj(el.cpfCnpj) === cpfCnpjProjeto;
        }) ||
        this.clienteRuralLkp.find(el => {
          const nomeLista = this.normalizeTexto(el.name || "");
          return nomeProjeto && nomeLista === nomeProjeto;
        }) ||
        this.clienteRuralLkp.find(el => {
          const nomeListaSemCodigo = this.normalizeTexto(
            (el.name || "").split(" [")[0]
          );
          return nomeProjeto && nomeListaSemCodigo === nomeProjeto;
        }) ||
        this.clienteRuralLkp.find(el =>
          this.projetoContemDadosDoCliente(valoresProjeto, el)
        ) ||
        null
      );
    },

    extrairValoresProjeto(projeto) {
      return Object.values(projeto)
        .filter(
          value =>
            value !== null &&
            value !== undefined &&
            ["string", "number"].includes(typeof value)
        )
        .map(value => `${value}`);
    },

    projetoContemDadosDoCliente(valoresProjeto, cliente) {
      const codigoCliente = `${cliente?.value || ""}`;
      const cpfCliente = this.normalizeCpfCnpj(cliente?.cpfCnpj || "");
      const nomeCompleto = this.normalizeTexto(cliente?.name || "");
      const nomeSemCodigo = this.normalizeTexto(
        (cliente?.name || "").split(" [")[0]
      );

      return valoresProjeto.some(valor => {
        const valorTexto = `${valor}`;
        const valorNormalizado = this.normalizeTexto(valorTexto);
        const valorCpf = this.normalizeCpfCnpj(valorTexto);

        return (
          (codigoCliente &&
            (valorTexto === codigoCliente ||
              valorTexto.includes(`[${codigoCliente}]`))) ||
          (cpfCliente && valorCpf === cpfCliente) ||
          (nomeCompleto && valorNormalizado === nomeCompleto) ||
          (nomeSemCodigo && valorNormalizado === nomeSemCodigo)
        );
      });
    },

    extrairCodigoClienteRural(cliente) {
      const match = (cliente?.name || "").match(/\[(\d+)\]/);
      return match ? match[1] : "";
    },

    normalizeTexto(value) {
      return (value || "")
        .toString()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, " ")
        .trim()
        .toUpperCase();
    },

    async getNomeRazaoUsuario() {
      try {
        this.cdPessoa = this.$store.state.me?.cd_pessoa;
        this.sqUsuario = this.$store.state.me?.sq_usuario;
      } catch {
        this.logoutUser();
      }
    },

    async verificarCooperado() {
      if (this.cooperadoLkp.length > 0) {
        const cooperadoEncontrado = this.cooperadoLkp.find(
          cooperado => cooperado.value === this.cdPessoa
        );

        if (cooperadoEncontrado) {
          this.selecaoCooperado = cooperadoEncontrado;
          this.carregarInstFinanceira();
        } else {
          this.selecaoCooperado = null;
        }
      } else {
        this.selecaoCooperado = null;
      }
    },

    async carregarClienteRural() {
      if (this.selecaoCooperado) {
        this.clienteRuralLkp = [];
        this.selecaoClienteRural = null;
        this.loadingClienteRural = true;

        try {
          const response = await this.$http.get(
            "/api/astec/getLkpClienteRuralProjetos/" +
              this.selecaoCooperado.value
          );
          this.clienteRuralLkp = response.data.map(cliente => {
            return {
              name: cliente.DISPLAY,
              value: cliente.ID,
              cpfCnpj: cliente.NRCPFCNPJ
            };
          });
        } catch (err) {
          this.$tools.alerta(err.response.data.message, "error");
        }

        this.loadingClienteRural = false;
        await this.carregarInstFinanceira();
      }
    },

    async carregarInstFinanceira() {
      if (!this.selecaoCooperado?.value) {
        return;
      }

      this.instituicaoFinanceiraLkp = [];
      this.instituicaoAgenciaLkp = [];
      this.selecaoInstituicaoFinanceira = null;
      this.selecaoAgencia = null;
      this.loadingInstFinanceira = true;

      try {
        await this.buscarLkpInstituicaoFinanc(this.selecaoCooperado.value);
      } catch (error) {
        this.$tools.alerta(
          "Erro ao carregar instituições financeiras",
          "error"
        );
      } finally {
        this.loadingInstFinanceira = false;
      }
    },

    async carregarAgencia() {
      if (this.selecaoInstituicaoFinanceira) {
        this.selecaoAgencia = null;
        this.loadingAgencia = true;
        this.cdCooperadoSelecionado = this.selecaoCooperado.value;

        try {
          const response = await this.$http.get(
            "/api/astec/getLkpAgencia/" +
              this.cdCooperadoSelecionado +
              "/" +
              this.selecaoInstituicaoFinanceira.value
          );

          this.instituicaoAgenciaLkp = response.data.map(nrAgencia => ({
            name: nrAgencia.DISPLAY,
            value: nrAgencia.ID
          }));
        } catch (err) {
          this.$tools.alerta(err.response.data.message, "error");
        }

        this.loadingAgencia = false;
      } else {
        this.instituicaoAgenciaLkp = [];
        this.selecaoAgencia = null;
      }
    },

    async carregarItemAgropecuario() {
      if (this.selecaoTipoProjeto && !this.editandoProjeto) {
        this.perAstec = this.selecaoTipoProjeto.perAstec;
        // Atualizar o percentualDisplay dinamicamente e recalcular vlAstec
        this.calculoAstec.setValoresIniciais(
          this.vlOrcado,
          this.perAstec,
          null
        );
      }

      if (this.selecaoTipoProjeto && this.selecaoSafra) {
        this.selecaoItemAgropecuario = null;
        this.loadingItemAgropecuario = true;

        try {
          const response = await this.$http.get(
            "/api/astec/getLkpItemAgropecuario/" +
              this.selecaoTipoProjeto.value +
              "/" +
              this.selecaoSafra.epoca
          );

          this.itemAgropecuarioLkp = response.data.map(itemAgropecuario => ({
            name: itemAgropecuario.DISPLAY,
            value: itemAgropecuario.ID
          }));
        } catch (err) {
          this.$tools.alerta(err.response.data.message, "error");
        }

        this.loadingItemAgropecuario = false;
      } else {
        this.selecaoItemAgropecuario = null;
        this.itemAgropecuarioLkp = [];
      }
    },

    duplicarProjeto() {
      this.loading = true;
      this.sqProjetoVar = null;
      this.cdProjeto = 30;
      this.statusProjeto = "ELABORADO";
      this.numeroProjeto = "";
      this.dataProjeto = "";
      this.selecaoSafra = "";
      this.nrOperacao = "";

      this.calculoAstec.reset();

      this.vlLiberado = "";
      this.dataCredito = "";
      this.nOperacao = "";
      this.dataVencBoleto = "";
      this.titleHeader = "Duplicando Projeto Astec";

      setTimeout(() => {
        this.loading = false;
        this.disabledCampos = false;
      }, 1000);
    },

    insertProjeto(acao) {
      if (this.retornoInsertProjeto) {
        const houveAlteracao =
          this.getSnapshotParaComparacao() !== this.dadosAoCarregar;

        if (!houveAlteracao && acao === "voltar") {
          this.$router.push("/astec/projetos");
          return;
        }
      }

      this.loadingGeral = true;
      this.loadingSalvarNormal = false;
      this.loadingSalvarVoltar = false;
      this.loadingSalvarExportar = false;
      this.loadingSalvarContinuar = false;

      const mapaAcoes = {
        salvar: "loadingSalvarNormal",
        voltar: "loadingSalvarVoltar",
        exportar: "loadingSalvarExportar",
        continuar: "loadingSalvarContinuar"
      };

      this[mapaAcoes[acao]] = true;

      this.submit = true;
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.loadingGeral = false;
        this[mapaAcoes[acao]] = false;
        return;
      }

      const obj = this.montarObjeto();

      if (this.importacao) {
        const sqAstecIntegracaoItem = this.$store.state.projetoAstec
          .SQ_ASTEC_INTEGRACAO_ITEM;
        const sqAstecIntegracaoLote = this.$store.state.projetoAstec
          .SQ_ASTEC_INTEGRACAO_LOTE;

        this.$http
          .put(
            `/api/astec/projetos/importarProjetos/projetosLote/${sqAstecIntegracaoItem}/${sqAstecIntegracaoLote}`,
            { ...obj, acao }
          )
          .then(response => {
            Swal.fire({
              icon: "success",
              title: "Sucesso",
              text: response.data.message
            });

            this.retornoInsertProjeto = true;
            this.dadosAoCarregar = this.getSnapshotParaComparacao();

            localStorage.removeItem("exibirCamposImportacao");
            this.$router.push("/astec/projetos/lotesImportacao/projetosLote");
          })
          .catch(error => {
            const msg =
              error.response?.data?.message || "Erro ao salvar projeto";
            this.$tools.alerta(msg, "error");
          })
          .finally(() => {
            this.loadingGeral = false;
            this[mapaAcoes[acao]] = false;
          });

        return;
      }

      this.$http
        .post("/api/astec/insertProjetos", obj)
        .then(response => {
          Swal.fire({
            icon: "success",
            title: "Sucesso",
            text: `Projeto ${response.data[0].ID} salvo com sucesso!`
          });

          this.sqProjetoVar = response.data[0].ID;

          this.retornoInsertProjeto = true;
          this.dadosAoCarregar = this.getSnapshotParaComparacao();

          if (acao === "voltar") {
            this.$router.push("/astec/projetos");
          } else if (acao === "continuar") {
            this.limparFormCadastro();
          }
        })
        .catch(err => {
          const msg = err.response?.data?.message || "Erro ao salvar projeto";
          this.$tools.alerta(msg, "error");
        })
        .finally(() => {
          this.loadingGeral = false;
          this[mapaAcoes[acao]] = false;
        });
    },

    montarObjeto() {
      const dados = {
        sqUsuario: this.sqUsuario,
        sqProjetoVar: this.sqProjetoVar,
        cdProjeto: this.cdProjeto,
        cdCooperado: this.selecaoCooperado ? this.selecaoCooperado.value : null,
        sqCliente: this.selecaoClienteRural
          ? this.selecaoClienteRural.value
          : null,
        nrPlano: this.numeroProjeto ? this.numeroProjeto : null,
        dtProjeto: this.dataProjeto ? this.dataProjeto : null,
        sqLinhaCredito: this.selecaoLinhaCredito
          ? this.selecaoLinhaCredito.value
          : null,
        sqTpProjeto: this.selecaoTipoProjeto
          ? this.selecaoTipoProjeto.value
          : null,
        sqModalidadeSeguro: this.selecaoModalidadeSeguro
          ? this.selecaoModalidadeSeguro.value
          : null,
        cdItemAgropecuario: this.selecaoItemAgropecuario
          ? parseInt(this.selecaoItemAgropecuario.value)
          : null,
        sqRegiao: this.selecaoMunicipio ? this.selecaoMunicipio.value : null,
        cidade: this.selecaoMunicipio
          ? this.selecaoMunicipio.name.split(" - ")[0]
          : null,
        estado: this.selecaoMunicipio
          ? this.selecaoMunicipio.name.split(" - ")[1]
          : null,
        cdSafra: this.selecaoSafra ? this.selecaoSafra.name : null,
        qtArea: this.areaHa ? this.areaHa : null,
        cdOperacaoCred: this.nOperacao ? this.nOperacao : null,
        vlOrcado: this.vlOrcado ? this.vlOrcado : null,
        vlAstec: this.vlAstec ? this.vlAstec : 0,
        dtCredito: this.dataCredito ? this.dataCredito : null,
        cdBanco: this.selecaoInstituicaoFinanceira
          ? this.selecaoInstituicaoFinanceira.value
          : null,
        nrAgencia: this.selecaoAgencia ? this.selecaoAgencia.value : null,
        dtVctoFinanc: this.dataVencBoleto ? this.dataVencBoleto : "",
        cdInstituicaoFinanceira: this.selecaoInstituicaoFinanceira
          ? this.selecaoInstituicaoFinanceira.value
          : null
      };

      return dados;
    },

    async voltar() {
      const houveAlteracao =
        this.getSnapshotParaComparacao() !== this.dadosAoCarregar;

      if (!houveAlteracao) {
        localStorage.removeItem("exibirCamposImportacao");

        this.importacao
          ? this.$router.push("/astec/projetos/lotesImportacao/projetosLote")
          : this.$router.push("/astec/projetos");

        return;
      }

      Swal.fire({
        icon: "warning",
        title: "ATENÇÃO",
        text:
          "Caso confirme, os dados cadastrados serão perdidos. Deseja sair sem salvar?",
        showCancelButton: true,
        confirmButtonText: "Sim",
        cancelButtonText: "Não"
      }).then(result => {
        if (result.isConfirmed) {
          localStorage.removeItem("exibirCamposImportacao");

          this.importacao
            ? this.$router.push("/astec/projetos/lotesImportacao/projetosLote")
            : this.$router.push("/astec/projetos");
        }
      });
    },

    formatCpfCnpj(option) {
      return option.cpfCnpj;
    },

    normalizeCpfCnpj(value) {
      if (!value) return null;
      return value.replace(/\D/g, "");
    },

    updateSearch(query) {
      this.searchQuery = query;
    },

    limparFormCadastro() {
      this.sqProjetoVar = "";
      this.cdProjeto = 30;
      this.selecaoClienteRural = "";
      this.numeroProjeto = "";
      this.dataProjeto = "";
      this.selecaoLinhaCredito = "";
      this.selecaoTipoProjeto = "";
      this.selecaoModalidadeSeguro = "";
      this.selecaoItemAgropecuario = "";
      this.selecaoMunicipio = "";
      this.selecaoSafra = "";
      this.areaHa = "";
      this.nOperacao = "";

      this.calculoAstec.reset();

      this.dataCredito = "";
      this.selecaoInstituicaoFinanceira = "";
      this.selecaoAgencia = "";
      this.dataVencBoleto = "";
      this.selecaoInstituicaoFinanceira = "";
      this.submit = false;
    },

    gerarBoleto() {
      this.loadingBoleto = true;
      this.loadingGeral = true;

      this.$http
        .get(
          `/api/astec/projetos/gerarBoleto/${this.sqProjetoVar}/${this.selecaoCooperado.value}`,
          {
            responseType: "blob"
          }
        )
        .then(response => {
          const blob = new Blob([response.data], { type: "application/pdf" });
          this.urlPagamento = URL.createObjectURL(blob);
          this.abreModal = true;
        })
        .catch(async error => {
          if (error.response && error.response.data instanceof Blob) {
            const text = await error.response.data.text();
            const message = JSON.parse(text);

            this.$tools.alerta(
              message.message || "Erro ao gerar boleto do projeto",
              "error"
            );
          } else {
            this.$tools.alerta("Erro ao gerar boleto do projeto", "error");
          }
        })
        .finally(() => {
          this.loadingBoleto = false;
          this.loadingGeral = false;
        });
    },

    gerarPix() {
      this.loadingPix = true;
      this.loadingGeral = true;

      this.$http
        .post(`/api/astec/projetos/gerarPix/${this.sqProjetoVar}`, {
          valor: this.vlAstec
        })
        .then(response => {
          if (response.status === 200) {
            this.qrCodeImage = response.data.qrCode;
            this.textAreaData = response.data.copiaCola || "";

            this.dadosPixDoProjeto = mapearDadosFormularioProjeto(this);

            this.mostraQRCode = true;
          }
        })
        .catch(error => {
          const messageError = error?.response?.data?.message;
          if (error.response.data.erro == true) {
            this.$tools.alerta(error.response.data.mensagem, "error");
          } else {
            this.$tools.alerta(
              messageError || "Erro ao gerar pix do projeto",
              messageError ? "warning" : "error"
            );
          }
        })
        .finally(() => {
          this.loadingPix = false;
          this.loadingGeral = false;
        });
    },

    openModalMovimentacao() {
      this.showModalMovimentacao = true;
      this.buscaMovimentacoes();
    },

    async buscaMovimentacoes() {
      const data = await MovimentacaoService.getByProjeto(this.sqProjetoVar);
      if (data) {
        this.retornoMovimentacao = data;
      }
    },

    statusTipoProjeto(value) {
      const status = ["LIBERADO", "LIQUIDADO", "BAIXA PARCIAL"];
      return status.includes(value);
    },

    houveAlteracao() {
      const atual = this.getSnapshotParaComparacao();
      return atual !== this.dadosAoCarregar;
    }
  }
};
</script>

<style lang="scss">
.is-valid .v-money {
  border-color: #34c38f !important;
}
.is-invalid .v-money {
  border-color: #f46a6a !important;
}

.opacity-date {
  opacity: 0.6;
  pointer-events: none;
}

.button-style {
  min-width: 155px;
  min-height: 40px;
}
</style>
